import { useTranslation } from "@sagot/i18n";
import { Button, Input, toast } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useFormatters } from "../shared/format";
import type { NameOf } from "./lead-activity";
import type { Activity } from "./lead-types";
import { useAddNote } from "./use-leads";

const NOTE_MAX = 4000;
const noteSchema = z.object({ body: z.string().trim().min(1, "noteRequired").max(NOTE_MAX) });
type NoteValues = z.infer<typeof noteSchema>;

interface LeadNotesProps {
  leadId: string;
  activities: readonly Activity[];
  nameOf: NameOf;
  canEdit: boolean;
}

/** Notes list (accent boxes) + "Add a note" form → `POST /v1/leads/{id}/activities {type: note}`. */
export function LeadNotes({ leadId, activities, nameOf, canEdit }: LeadNotesProps) {
  const { t } = useTranslation();
  const format = useFormatters();
  const errorMessage = useErrorMessage();
  const addNote = useAddNote(leadId);
  const form = useForm<NoteValues>({ resolver: zodResolver(noteSchema), defaultValues: { body: "" } });
  const notes = activities.filter((a) => a.type === "note").sort((a, b) => a.created_at.localeCompare(b.created_at));
  const error = form.formState.errors.body;

  const onSubmit = form.handleSubmit(async ({ body }) => {
    try {
      await addNote.mutateAsync(body);
      form.reset({ body: "" });
      toast.success(t("engage.leads.sheet.noteAdded"));
    } catch (caught) {
      toast.error(errorMessage(caught));
    }
  });

  return (
    <section aria-labelledby="lead-notes-title">
      <h3 id="lead-notes-title" className="label">
        {t("engage.leads.sheet.notes")}
      </h3>
      <div className="space-y-2">
        {notes.length === 0 ? <p className="text-sm text-fg-subtle">{t("engage.leads.sheet.noNotes")}</p> : null}
        {notes.map((note) => (
          <div key={note.id} className="rounded-md bg-accent-soft px-3 py-2 text-sm">
            <div className="text-[11px] font-semibold text-accent-ink">
              {[nameOf(note.actor_user_id), format.dateTime(note.created_at)].filter(Boolean).join(" · ")}
            </div>
            <p className="whitespace-pre-wrap">{note.body}</p>
          </div>
        ))}
      </div>
      {canEdit ? (
        <form className="mt-2" noValidate onSubmit={(event) => void onSubmit(event)}>
          <div className="flex gap-2">
            <Input
              aria-label={t("engage.leads.sheet.noteLabel")}
              placeholder={t("engage.leads.sheet.notePlaceholder")}
              maxLength={NOTE_MAX}
              invalid={Boolean(error)}
              aria-describedby={error ? "lead-note-error" : undefined}
              {...form.register("body")}
            />
            <Button type="submit" variant="secondary" loading={addNote.isPending}>
              {t("engage.leads.sheet.addNote")}
            </Button>
          </div>
          {error ? (
            <p id="lead-note-error" role="alert" className="mt-1 text-xs font-semibold text-danger">
              {t("engage.leads.sheet.noteRequired")}
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
